"use client";

import { useTranslation } from "react-i18next";
import { History, Loader2 } from "lucide-react";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { useRecordRevisions } from "@/hooks/modal/useRecordRevisions";
import type { RevisionItem } from "@/lib/types/revisions";

/**
 * Format ISO timestamp to HH:mm format
 */
function formatTime(changedAt: string): string {
    try {
        const date = new Date(changedAt);
        return date.toLocaleTimeString("ru-RU", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
}

/**
 * Format ISO timestamp to date string for grouping
 */
function formatDateGroup(changedAt: string, t: (key: string, defaultValue: string) => string): string {
    try {
        const date = new Date(changedAt);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return t("changes.date.today", "Today");
        }
        if (date.toDateString() === yesterday.toDateString()) {
            return t("changes.date.yesterday", "Yesterday");
        }

        return date.toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return "";
    }
}

/**
 * Format a label by splitting on commas for better readability.
 */
function formatLabelParts(label: string): React.ReactNode {
    const parts = label.split(", ");
    if (parts.length === 1) {
        return label;
    }
    return (
        <>
            {parts[0]}
            {parts.slice(1).map((part, i) => (
                <span key={i} className="block pl-3">
                    {part}
                </span>
            ))}
        </>
    );
}

interface RevisionRowProps {
    revision: RevisionItem;
}

function RevisionRow({ revision }: RevisionRowProps) {
    const { t } = useTranslation();

    return (
        <div className="py-3 px-4 hover:bg-gray-50/50 transition-colors border-b last:border-b-0">
            {/* Header row: name + type, time */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-gray-900">
                        {revision.changed_by.name}
                        <span className="font-normal text-gray-500 ml-1">
                            — {revision.changed_by.type === "user"
                                ? t("changes.user_type.admin", "admin")
                                : t("changes.user_type.client", "client")}
                        </span>
                    </span>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                    {formatTime(revision.changed_at)}
                </span>
            </div>
            {/* Labels - bullet list */}
            <ul className="text-sm text-gray-600 space-y-0.5 pl-1">
                {revision.labels.map((label, idx) => (
                    <li key={idx} className="flex items-start">
                        <span className="text-gray-400 mr-2">•</span>
                        <span>{formatLabelParts(label)}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

interface GroupedRevisions {
    date: string;
    items: RevisionItem[];
}

export function ChangesSection() {
    const { t } = useTranslation();
    const { appointment } = useAppointmentModal();

    const recordId = appointment?.id ? Number(appointment.id) : undefined;
    const { data: revisions = [], isLoading, error } = useRecordRevisions(recordId);

    // Group revisions by date
    const groupedRevisions: GroupedRevisions[] = [];
    if (revisions.length > 0) {
        const groups: Record<string, RevisionItem[]> = {};
        revisions.forEach((revision) => {
            const dateKey = formatDateGroup(revision.changed_at, t);
            if (!groups[dateKey]) {
                groups[dateKey] = [];
            }
            groups[dateKey].push(revision);
        });
        Object.entries(groups).forEach(([date, items]) => {
            groupedRevisions.push({ date, items });
        });
    }

    if (isLoading) {
        return (
            <div className="flex-1 overflow-y-auto flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                <span className="text-gray-500">{t("changes.loading", "Loading change history...")}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 overflow-y-auto flex items-center justify-center text-red-500">
                <span>{t("changes.error", "Error loading change history")}</span>
            </div>
        );
    }

    if (revisions.length === 0) {
        return (
            <div className="flex-1 overflow-y-auto flex flex-col items-center justify-center text-gray-400">
                <History className="w-12 h-12 mb-3 stroke-[1.5]" />
                <span className="text-sm">{t("changes.empty", "There are no changes yet")}</span>
            </div>
        );
    }

    return (
        <div className="flex-1 overflow-y-auto">
            <div className="p-6">
                {/* Section header */}
                <div className="mb-4">
                    <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
                        <History className="w-5 h-5 text-gray-500" />
                        {t("changes.title", "Changes history")}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {t("changes.description", "All changes for the current record")}
                    </p>
                </div>

                {/* Revisions list grouped by date */}
                <div className="border rounded-lg bg-white">
                    {groupedRevisions.map((group) => (
                        <div key={group.date}>
                            {/* Date header */}
                            <div className="py-2 px-4 bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wide border-b">
                                {group.date}
                            </div>
                            {/* Revisions for this date */}
                            {group.items.map((revision) => (
                                <RevisionRow key={revision.id} revision={revision} />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
